/**
 * YouTube Data API v3 - channel search and channel videos
 * Used for gallery video search (no backend proxy; uses VITE_YOUTUBE_API_KEY)
 */

const YOUTUBE_API_BASE = "https://www.googleapis.com/youtube/v3";
const API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;

export interface YouTubeChannel {
  id: string;
  title: string;
  description: string;
  thumbnails: { default?: { url: string }; medium?: { url: string }; high?: { url: string } };
}

export interface YouTubeVideoItem {
  id: string;
  videoId: string;
  title: string;
  url: string;
  platform: "youtube";
  thumbnails?: { default?: { url: string }; medium?: { url: string }; high?: { url: string } };
}

function getThumbUrl(thumbnails: YouTubeChannel["thumbnails"]): string {
  return thumbnails?.high?.url ?? thumbnails?.medium?.url ?? thumbnails?.default?.url ?? "";
}

export async function searchChannels(query: string, pageToken?: string): Promise<{
  channels: YouTubeChannel[];
  nextPageToken?: string;
  prevPageToken?: string;
}> {
  if (!API_KEY) throw new Error("VITE_YOUTUBE_API_KEY is not set");
  const params = new URLSearchParams({
    part: "snippet",
    type: "channel",
    maxResults: "10",
    q: query.trim(),
    key: API_KEY,
  });
  if (pageToken) params.set("pageToken", pageToken);
  const res = await fetch(`${YOUTUBE_API_BASE}/search?${params}`);
  if (!res.ok) throw new Error("YouTube channel search failed");
  const data = await res.json();
  const channels: YouTubeChannel[] = (data.items ?? []).map((item: any) => ({
    id: item.snippet?.channelId ?? item.id?.channelId ?? "",
    title: item.snippet?.title ?? "",
    description: item.snippet?.description ?? "",
    thumbnails: item.snippet?.thumbnails ?? {},
  }));
  return {
    channels,
    nextPageToken: data.nextPageToken,
    prevPageToken: data.prevPageToken,
  };
}

export async function getChannelVideos(
  channelId: string,
  pageToken?: string
): Promise<{
  videos: YouTubeVideoItem[];
  nextPageToken?: string;
  prevPageToken?: string;
}> {
  if (!API_KEY) throw new Error("VITE_YOUTUBE_API_KEY is not set");
  const params = new URLSearchParams({
    part: "snippet",
    channelId,
    type: "video",
    maxResults: "10",
    key: API_KEY,
  });
  if (pageToken) params.set("pageToken", pageToken);
  const res = await fetch(`${YOUTUBE_API_BASE}/search?${params}`);
  if (!res.ok) throw new Error("YouTube channel videos fetch failed");
  const data = await res.json();
  const videos: YouTubeVideoItem[] = (data.items ?? [])
    .filter((item: any) => item.id?.videoId)
    .map((item: any) => {
      const videoId = item.id.videoId;
      return {
        id: videoId,
        videoId,
        title: item.snippet?.title ?? "",
        url: `https://www.youtube.com/watch?v=${videoId}`,
        platform: "youtube" as const,
        thumbnails: item.snippet?.thumbnails,
      };
    });
  return {
    videos,
    nextPageToken: data.nextPageToken,
    prevPageToken: data.prevPageToken,
  };
}

export { getThumbUrl as getYouTubeThumbUrl };
